import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import { authConfig } from './auth';

/**
 * Microsoft Graph sign-in for the standalone `?sp=1` SharePoint demo — a SEPARATE MSAL client from
 * lib/auth.ts, and the ONLY surface in this app that holds a Graph token.
 *
 * NOT THE WORKSPACE'S SIGN-IN, not any more. The bid-site picker and pane used to open a silent
 * session here on mount and browse Graph from the browser; they now go through Niobe's backend,
 * which calls Graph on the member's behalf (lib/graphSites.ts). Everything that existed to make
 * that second sign-in invisible — the silent session, its one automatic redirect to Entra, the flow
 * flag saying which surface to come back to — went with it. What remains is the demo, which is its
 * own page with no Niobe sign-in behind it and therefore a real "Connect Microsoft 365" button.
 *
 * WHY separate: lib/auth.ts signs in to the Niobe backend API (audience `api://<niobeClientId>`).
 * Microsoft Graph rejects that token — it needs a token whose audience is Graph, carrying the
 * SharePoint scopes. The SAME app registration mints both, but each resource wants its own default
 * scope + redirect config, so Graph keeps its own PublicClientApplication. The two never race over
 * a redirect response: main.tsx routes them into mutually exclusive page loads (?sp=1 or a graph
 * return → this module only; every other load → lib/auth.ts only). Sharing the client id is in fact
 * a bonus — the MSAL sessionStorage cache is keyed by client id, so a member who connected here is
 * already a known account for the main app in that tab.
 *
 * WHY the Niobe app: IT admin-consented delegated `Sites.ReadWrite.All` on `FR_APPS_Niobe Deal
 * Tracker` (5b2c7e9e…) on 2026-07-29 — verified end to end (the minted token's `scp` carries it and
 * the deployed niobe-mcp-graph returned real SharePoint data). This previously defaulted to a
 * different app, `FR_APPS_Acid Deal Tracker` (11d1250a…), purely because that one already had the
 * consent; that borrowed identity is no longer needed and coupling Niobe to another team's
 * production app was never the intent. Delegated either way: the token acts AS the signed-in member,
 * so SharePoint per-user ACL is preserved (the app can only reach sites the member can).
 *
 * WHY redirect, not popup: the popup→opener handoff (the opener reading the auth code back out of
 * the popup) proved unreliable here — loginPopup opened the window and never resolved even though
 * the popup landed on the redirect page with the code intact. Redirect keeps everything in ONE
 * window: the PKCE verifier and the returned code share the same sessionStorage, so there is no
 * cross-window read to fail. The redirect URI is the app origin, registered as a SPA redirect on the
 * Niobe app for every Niobe origin (localhost:5173/5180, both SWA hosts, niobe.superbox.*);
 * main.tsx detects the return and renders this demo so MSAL can finish and navigate back to ?sp=1.
 *
 * All ids/scopes are public config, overridable via window.__NIOBE_CONFIG__.graph or VITE_GRAPH_*.
 */

export interface GraphConfig {
  tenantId: string;
  clientId: string;
  scopes: string[];
}

/** FR_APPS_Niobe Deal Tracker — Niobe's own app; delegated Sites.ReadWrite.All admin-consented. */
const DEFAULT_CLIENT_ID = '5b2c7e9e-f11e-466d-8e9e-c2277e5a61a0';
const DEFAULT_TENANT_ID = '76a2ae5a-9f00-4f6b-95ed-5d33d77c4d61';
const DEFAULT_SCOPES = ['https://graph.microsoft.com/Sites.ReadWrite.All'];

/**
 * The Graph client id, tenant and scopes.
 *
 * The clientId falls back to the API sign-in's OWN registration (lib/auth.ts) before the baked-in
 * default. MSAL keys its token cache by client id, so sharing it means a member who signed in to
 * Niobe in this tab is already a known account here, and `acquireTokenSilent` can redeem the
 * refresh token minted then for a Graph token — no iframe, no prompt, no redirect. That mattered
 * most when the workspace itself needed Graph; the demo now benefits from it, without depending on
 * it, since it has an explicit connect button for when there is no such account to borrow.
 */
export function graphConfig(): GraphConfig {
  const g = window.__NIOBE_CONFIG__?.graph;
  const auth = authConfig();
  const envScopes = import.meta.env.VITE_GRAPH_SCOPES?.split(/\s+/).filter(Boolean);
  return {
    clientId: g?.clientId ?? import.meta.env.VITE_GRAPH_CLIENT_ID ?? auth?.clientId ?? DEFAULT_CLIENT_ID,
    tenantId: g?.tenantId ?? import.meta.env.VITE_GRAPH_TENANT_ID ?? auth?.tenantId ?? DEFAULT_TENANT_ID,
    scopes: g?.scopes ?? envScopes ?? DEFAULT_SCOPES,
  };
}

/**
 * Is this silent failure one a full-page redirect would fix?
 *
 * `interaction_required` is the honest answer from Entra. `timed_out` is the hidden-iframe trap
 * lib/auth.ts documents: a browser that partitions third-party frames never lets the iframe reach
 * login.microsoftonline.com, so the silent call hangs rather than refusing. Both mean "ask Entra in
 * the top-level window"; anything else is a real fault and is rethrown so it can be seen.
 */
function needsInteraction(e: unknown): boolean {
  return (
    e instanceof InteractionRequiredAuthError ||
    (e instanceof BrowserAuthError && e.errorCode === BrowserAuthErrorCodes.timedOut)
  );
}

let instance: PublicClientApplication | null = null;
let initialized = false;
// Deduped init promise: React StrictMode double-invokes effects in dev, so two ensureGraphReady()
// calls can race — without this both would run handleRedirectPromise and one could consume the auth
// code before the other, dropping the sign-in. All callers await the same init.
let initPromise: Promise<void> | null = null;

async function client(cfg: GraphConfig): Promise<PublicClientApplication> {
  if (!instance) {
    const configuration: Configuration = {
      auth: {
        clientId: cfg.clientId,
        authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
        // The DEFAULT reply URL — used by the SILENT hidden iframe (ssoSilent /
        // acquireTokenSilent). A blank page, NOT the app root: loading the whole SPA inside that
        // iframe re-runs the app there and drops the `#code` before MSAL reads it, so the silent
        // call times out instead of succeeding (the same trap lib/auth.ts documents). The
        // interactive redirects below override it back to the app origin per request, which is
        // where main.tsx expects the return.
        redirectUri: `${window.location.origin}/blank.html`,
        postLogoutRedirectUri: window.location.origin,
      },
      // sessionStorage: per-tab, mirrors lib/auth.ts and avoids leaking a login across tabs.
      cache: { cacheLocation: 'sessionStorage' },
    };
    instance = new PublicClientApplication(configuration);
  }
  const msal = instance;
  if (!initPromise) {
    initPromise = (async () => {
      await msal.initialize();
      // handleRedirectPromise processes the returned auth code on a redirect return, and also clears
      // any leftover interaction state from a prior interrupted sign-in.
      //
      // REDEEM THE CODE WHERE IT LANDS. `navigateToLoginRequestUrl: false` is why this no longer
      // takes two clicks, and it is not a preference — MSAL's default (`true`) is actively wrong
      // for this app's shape.
      //
      // The default means: compare the current URL with the URL the sign-in was started from, and
      // if they differ do NOT redeem the code — stash the response in sessionStorage, navigate to
      // that start URL, and trust whatever loads there to call handleRedirectPromise again. Our
      // interactive redirects reply to the app ORIGIN while the click can happen anywhere, so they
      // can differ. The stashed response then rode back with no `code=` left in the URL — which is
      // exactly what main.tsx tests to recognise a Graph return, so it booted the ordinary app,
      // nothing completed the sign-in, and the member's SECOND click was what finally ran
      // handleRedirectPromise on the matching URL and redeemed a code from the round trip before.
      //
      // Worse, that stash is keyed `msal.<clientId>.<key>` — no correlation id — and lib/auth.ts
      // shares the client id. On that ordinary boot AuthGate runs FIRST, so it could redeem OUR
      // Graph code and hand its Graph-audience token back as the Niobe API bearer, 401ing the
      // backend until the next top-up.
      //
      // `false` collapses all of it: the code is redeemed on the reply-URL load, where main.tsx has
      // already routed us to the demo alone, and the demo navigates back itself from the URL
      // rememberFlow() recorded. One hop, one redeemer, no shared-cache race.
      await withTimeout(
        msal.handleRedirectPromise({ navigateToLoginRequestUrl: false }),
        15_000,
        'handleRedirectPromise',
      ).catch((e) => {
        console.warn('[graphAuth] handleRedirectPromise:', e);
        return undefined;
      });
      initialized = true;
    })();
  }
  await initPromise;
  return msal;
}

/** Reject a hung promise so the UI shows a real error instead of spinning forever. */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<T>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s — see the browser console.`)), ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/**
 * Set right before a redirect so main.tsx recognises the app-root load on return as OUR sign-in.
 *
 * Its PRESENCE is the whole signal now. It used to carry which surface had asked — `'app'` for the
 * bid-site picker, `'1'` for the demo — because both redirected to the same reply URL and main.tsx
 * had to route the return to the right one. The workspace no longer signs in to Graph, so there is
 * one surface left and one value: `'1'`, kept as the literal the demo has always written.
 */
const REDIRECT_FLAG = 'niobe.graphConnect';
/** Where to come back to once the sign-in completes — the demo repairs its own address bar with it. */
const RETURN_FLAG = 'niobe.graphReturnTo';

/**
 * Record that a Graph sign-in is in flight, and where it started.
 *
 * Every path that navigates away has to call this — a first sign-in AND a silent-refresh failure
 * alike — because with navigateToLoginRequestUrl off (see client()) MSAL no longer walks anyone
 * back to where they started. That walk was the hop that lost the code, so whoever completes the
 * sign-in owes the member the journey home.
 */
function rememberFlow(): void {
  sessionStorage.setItem(REDIRECT_FLAG, '1');
  sessionStorage.setItem(RETURN_FLAG, window.location.href);
}

/**
 * Start interactive sign-in via a full-page REDIRECT. Navigates away — nothing after this runs.
 * Completion happens on the return load via ensureGraphReady().
 *
 * The caller's current URL is recorded too: MSAL normally navigates back to it on its own, but it
 * skips that when the reply URL and the request URL already match, which for the app root they can
 * — so the return load finishes the job itself rather than leaving the member stranded on it.
 */
export async function connectGraph(): Promise<void> {
  const cfg = graphConfig();
  console.info('[graphAuth] connect (redirect): clientId=%s scopes=%o', cfg.clientId, cfg.scopes);
  const msal = await client(cfg);
  const existing = msal.getAllAccounts()[0] ?? null;
  if (existing) {
    msal.setActiveAccount(existing);
    return;
  }
  rememberFlow();
  await msal.loginRedirect({ scopes: cfg.scopes, redirectUri: window.location.origin });
}

/**
 * Consume the round trip: hand back where the member was, and retire the flags that describe it.
 * Falls back to the workspace root.
 *
 * The flag is cleared HERE, unconditionally, rather than only on a successful completion. A
 * `niobe.graphConnect` left set after a Graph sign-in that went nowhere is not inert: main.tsx
 * routes any later auth response on this tab by that flag, so a stale one would send lib/auth.ts's
 * OWN redirect return to the demo — letting the Graph client consume the API sign-in's code. The
 * round trip is over whether or not it worked, so say so.
 */
export function takeGraphReturnUrl(): string {
  const url = sessionStorage.getItem(RETURN_FLAG);
  sessionStorage.removeItem(RETURN_FLAG);
  sessionStorage.removeItem(REDIRECT_FLAG);
  return url ?? '/';
}

/**
 * Complete a redirect sign-in when returning from one, and report the connected account. Call this
 * on mount. client() runs handleRedirectPromise (processes the returned code); with
 * navigateToLoginRequestUrl (default) MSAL then navigates back to where connect was clicked, and on
 * that final load the account is in the cache.
 */
export async function ensureGraphReady(): Promise<AccountInfo | null> {
  const cfg = graphConfig();
  const msal = await client(cfg);
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (account) {
    msal.setActiveAccount(account);
    sessionStorage.removeItem(REDIRECT_FLAG);
    console.info('[graphAuth] connected as %s', account.username);
  }
  return account;
}

/** The connected account, or null. Synchronous — valid after ensureGraphReady() has run. */
export function graphAccount(): AccountInfo | null {
  if (!instance || !initialized) return null;
  return instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
}

/**
 * A Graph access token, refreshing silently when possible and falling back to a full-page redirect
 * when Entra requires interaction. Throws if the member has not connected yet.
 *
 * The demo is the only caller. Nothing in the workspace asks for a Graph token any more, which is
 * why the redirect below can afford to be a redirect: it takes a page whose whole purpose is this
 * sign-in away, not a member mid-way through pinning a bid site.
 */
export async function graphToken(): Promise<string> {
  const cfg = graphConfig();
  const msal = await client(cfg);
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (!account) throw new Error('Not connected to Microsoft 365. Click “Connect Microsoft 365” first.');
  try {
    const result = await msal.acquireTokenSilent({ scopes: cfg.scopes, account });
    return result.accessToken;
  } catch (e) {
    // The iframe timeout counts as well as a plain refusal — see needsInteraction. Treating it as
    // a hard failure left the browser reporting "timed out after 15s" at a member whose only
    // problem was a partitioned third-party frame, which one redirect fixes.
    if (needsInteraction(e)) {
      rememberFlow();
      await msal.acquireTokenRedirect({
        scopes: cfg.scopes,
        account,
        redirectUri: window.location.origin,
      });
      throw new Error('Redirecting to Microsoft 365 to refresh access…'); // page is navigating away
    }
    throw e;
  }
}
